import { useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { ApiError, getDocumentView, listPages } from '../Archive/api';
import { DocumentPage, DraftDocument } from '../Archive/types';
import { DOCUMENT_PAGES_KEY, DOCUMENT_VIEW_KEY } from './constants';

// What a document's record needs: the document with the path of its
// file (`GET /archives/document?id=`) and its pages (`GET /:id/pages`).
//
// Reading the document records a visit on the backend, and it is the one
// the record must log. That is why it is not requested again on window focus
// nor retried by itself: one visit per view, as before.
//
// A document archived through the previous path has no pages: its single
// file is presented as a synthetic page so that the same viewer
// shows it (PDF in <iframe>, image as-is).

export const LEGACY_PAGE_ID = 'legacy';

export const legacyPage = (
  document: DraftDocument,
  image: string,
): DocumentPage => ({
  id: LEGACY_PAGE_ID,
  document_id: document.id,
  position: 1,
  mime_type: image.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg',
  byte_size: 0,
  verification_url: null,
  image_url: image,
});

export type DocumentViewState = 'loading' | 'missing' | 'error' | 'ready';

export const useDocumentView = (id: string | undefined) => {
  const queryClient = useQueryClient();

  const view = useQuery(
    [DOCUMENT_VIEW_KEY, id],
    () => getDocumentView(id as string),
    { enabled: Boolean(id), retry: false, refetchOnWindowFocus: false },
  );

  const pages = useQuery(
    [DOCUMENT_PAGES_KEY, id],
    () => listPages(id as string),
    { enabled: Boolean(id), retry: false, refetchOnWindowFocus: false },
  );

  const state: DocumentViewState =
    view.isError
      ? view.error instanceof ApiError && view.error.status === 404
        ? 'missing'
        : 'error'
      : view.isLoading || pages.isLoading || !view.data
      ? 'loading'
      : 'ready';

  const document = view.data?.document ?? null;
  const image = view.data?.image ?? null;
  const serverPages = useMemo(() => pages.data ?? [], [pages.data]);
  const isLegacy = serverPages.length > 0 ? false : Boolean(image);

  // What the viewer shows: the pages, or the single file as a page.
  const viewerPages = useMemo<DocumentPage[]>(() => {
    if (serverPages.length > 0) return serverPages;
    if (document && image) return [legacyPage(document, image)];
    return [];
  }, [serverPages, document, image]);

  const refresh = () => {
    queryClient.invalidateQueries([DOCUMENT_VIEW_KEY, id]);
    queryClient.invalidateQueries([DOCUMENT_PAGES_KEY, id]);
  };

  return {
    state,
    document,
    image,
    pages: serverPages,
    viewerPages,
    isLegacy,
    refresh,
    retry: () => {
      view.refetch();
      pages.refetch();
    },
  };
};
